<template>
  <main class="min-h-screen bg-white">
    <HeaderImage :title="about.title" :image="about.image" />

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <!-- Company Description -->
      <section class="mb-10" ref="descriptionSection">
        <div class="prose prose-lg max-w-none" :class="{ 'animate-slideInUp': descriptionVisible }">
          <p class="text-lg leading-relaxed mb-6" v-html="about.company.description"></p>
        </div>
      </section>
        
        <!-- Statistics Row -->
        <section class="mb-10 bg-gray-900 text-white py-12 rounded-lg" ref="statsSection">
          <div class="max-w-7xl mx-auto px-4">
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center" :class="{ 'animate-slideInUp': descriptionVisible }">
              <div v-for="(stat, index) in about.stats" :key="index">
                <p class="text-3xl md:text-4xl font-bold" v-if="index <= 3">
                  {{ this.animatedNumbers[index] }}+
                </p>
                <p class="text-3xl md:text-4xl font-bold" v-else-if="index == 4">
                  {{ this.animatedNumbers[index] }}/4
                </p>
                <p class="text-3xl md:text-4xl font-bold" v-else>
                  {{ this.animatedNumbers[index] }}
                </p>
                <p class="text-sm mt-1">{{ stat.label }}</p>
              </div>
            </div>
          </div>
        </section>

      <!-- Mission & Solutions -->
      <section class="mb-10 grid grid-cols-1 md:grid-cols-2 gap-12" ref="missionSection">
        <div class="bg-blue-200 p-20 rounded-lg flex justify-center items-center text-center shadow-lg" :class="{ 'animate-slideInUp': missionVisible }">
          <p class="italic font-bold text-2xl" v-html="about.mission"></p>
        </div>
        <div :class="{ 'animate-slideInUp': missionVisible }">
          <p class="mb-4 text-justify" v-html="about.solutions.description"></p>
        </div>
      </section>

      <!-- Value Icons -->
      <section class="mb-10" ref="iconsSection">
        <p class="text-center text-2xl">MINE ITS facilitate the transformation of your data center from traditional to a</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" :class="{ 'animate-slideInUp': iconsVisible }">
          <div v-for="(item, index) in icons" :key="index" class="p-6 bg-white rounded-xl shadow-lg text-center" :class="{ 'animate-scaleInUp': iconsVisible }" :style="iconsVisible ? { 'animation-delay': (index * 0.1) + 's' } : {}">
            <img :src="require(`@/assets/a${index+1}.png`)" class="h-[70px] mx-auto" />
            <h3 class="text-lg font-bold">{{ item.name }}</h3>
          </div>
        </div>
      </section>

      <!-- Why Partner -->
      <section class="mb-10" ref="partnerSection">
        <h2 class="text-3xl font-bold mb-6">{{ about.whyPartner.title }}</h2>
        <p class="mb-4" v-html="about.whyPartner.continuousLifecycle"></p>
      </section>

      <!-- Accreditation Logos -->
      <section class="mb-10 relative" ref="logosSection">
        <h2 class="text-3xl md:text-4xl font-bold mb-3 text-center" :class="{ 'animate-slideInUp': logosVisible }">Accreditation</h2>

        <div class="relative">
          <!-- scroll container -->
          <div
            ref="logoWrapper"
            class="flex space-x-6 overflow-x-auto no-scrollbar py-4 items-center"
            @mouseenter="stopAutoScroll"
            @mouseleave="startAutoScroll"
            :class="{ 'animate-slideInUp': logosVisible }"
          >
            <div
              v-for="(logo, index) in duplicatedLogos"
              :key="index"
              class="w-1/5 flex-none text-center items-center"
              :class="{ 'animate-scaleIn': logosVisible }"
              :style="logosVisible ? { 'animation-delay': (index * 0.1) + 's' } : {}"
            >
              <!-- use modulo to select image since duplicates follow original order -->
              <img :src="require('@/assets/ac'+((index % about.accreditations.length)+1)+'.png')" class="mx-auto" />
            </div>
          </div>

          <!-- manual controls -->
          <button
            @mousedown="arrowDown(-200)" @mouseup="arrowUp" @mouseleave="arrowUp"
            class="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-75 p-2 rounded-full shadow"
          >
            <!-- left arrow icon -->
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 " fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            @mousedown="arrowDown(200)" @mouseup="arrowUp" @mouseleave="arrowUp"
            class="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-75 p-2 rounded-full shadow"
          >
            <!-- right arrow icon -->
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 " fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </section>
    </div>
  </main>
</template>

<script>
import HeaderImage from '@/components/HeaderImage.vue'

export default {
  name: 'AboutPage',
  components: {
    HeaderImage
  },
  data() {
    const aboutData = require('@/data/home.json').about
    return {
      about: aboutData,
      heroVisible: false,
      descriptionVisible: false,
      statsVisible: false,
      missionVisible: false,
      iconsVisible: false,
      partnerVisible: false,
      logosVisible: false,
      sectionObserver: null,
      autoScrollTimer: null,
      arrowTimer: null,
      icons: [
        { name: 'Scalability', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
        { name: 'Agility', icon: 'M14.752 11.168l-3.197-2.132A1 1 0 0010 10v4a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z' },
        { name: 'Sustainability', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
        { name: 'Technology Ready', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' }
      ],
      autoSlideDelay: 3000,
      animatedNumbers: [0, 0, 0, 0, 0, 0],
      animationSpeed: 50,
      hasAnimated: false,
      observer: null,
    }
  },
  mounted() {
    this.resetAnimation()
    this.setupIntersectionObserver()
    // show hero immediately
    this.heroVisible = true
    this.setupSectionObserver()
    // attach scroll listener for infinite manual scrolling
    if (this.$refs.logoWrapper) {
      this.$refs.logoWrapper.addEventListener('scroll', this.onLogoScroll)
    }
  },
  beforeUnmount() {
    if (this.sectionObserver) {
      this.sectionObserver.disconnect()
    }
    if (this.$refs.logoWrapper) {
      this.$refs.logoWrapper.removeEventListener('scroll', this.onLogoScroll)
    }
    // clear any running timers
    this.stopAutoScroll()
    this.stopArrowScroll()
  },
  computed: {
    duplicatedLogos() {
      // duplicate the sequence so we can scroll past the end and jump back silently
      return [...this.about.accreditations, ...this.about.accreditations]
    }
  },
  methods: {
    resetAnimation() {
      this.animatedNumbers = [0, 0, 0, 0, 0, 0]
      this.hasAnimated = false
    },
    setupIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }

      // if (!('IntersectionObserver' in window)) {
      setTimeout(() => {
        this.startNumberAnimation()
      }, 500)
      //   return
      // }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.hasAnimated) {
              this.startNumberAnimation()
              this.hasAnimated = true

              if (this.observer && entry.target) {
                this.observer.unobserve(entry.target)
              }
            }
          })
        },
        {
          threshold: 0.3,
          rootMargin: '0px 0px -100px 0px'
        }
      )

      this.$nextTick(() => {
        const section = this.$refs.aboutSection
        if (section) {
          this.observer.observe(section)
        }
      })
    },
    
    startNumberAnimation() {
      if (this.about.stats) {
        this.about.stats.forEach((target, index) => {
          this.animateNumber(index, target.value)
        })
      }
    },
    
    animateNumber(index, target) {
      const increment = target / 40
      let current = 0
      
      const timer = setInterval(() => {
        current += increment
        if (current >= target) {
          current = target
          clearInterval(timer)
        }
        this.animatedNumbers[index] = Math.floor(current)
      }, this.animationSpeed)
    },
    setupSectionObserver() {
      if (!('IntersectionObserver' in window)) {
        // fallback: reveal everything
        this.heroVisible = true
        this.descriptionVisible = true
        this.statsVisible = true
        this.missionVisible = true
        this.iconsVisible = true
        this.partnerVisible = true
        this.logosVisible = true
        return
      }

      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const ref = entry.target.getAttribute('data-section')
              if (ref === 'hero') this.heroVisible = true
              if (ref === 'description') this.descriptionVisible = true
              if (ref === 'stats') this.statsVisible = true
              if (ref === 'mission') this.missionVisible = true
              if (ref === 'icons') this.iconsVisible = true
              if (ref === 'partner') this.partnerVisible = true
              if (ref === 'logos') this.logosVisible = true
            }
          })
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        }
      )
      
      // Observe all sections
      if (this.$refs.heroSection) {
        this.$refs.heroSection.setAttribute('data-section', 'hero')
        this.sectionObserver.observe(this.$refs.heroSection)
      }
      if (this.$refs.descriptionSection) {
        this.$refs.descriptionSection.setAttribute('data-section', 'description')
        this.sectionObserver.observe(this.$refs.descriptionSection)
      }
      if (this.$refs.statsSection) {
        this.$refs.statsSection.setAttribute('data-section', 'stats')
        this.sectionObserver.observe(this.$refs.statsSection)
      }
      if (this.$refs.missionSection) {
        this.$refs.missionSection.setAttribute('data-section', 'mission')
        this.sectionObserver.observe(this.$refs.missionSection)
      }
      if (this.$refs.iconsSection) {
        this.$refs.iconsSection.setAttribute('data-section', 'icons')
        this.sectionObserver.observe(this.$refs.iconsSection)
      }
      if (this.$refs.partnerSection) {
        this.$refs.partnerSection.setAttribute('data-section', 'partner')
        this.sectionObserver.observe(this.$refs.partnerSection)
      }
      if (this.$refs.logosSection) {
        this.$refs.logosSection.setAttribute('data-section', 'logos')
        this.sectionObserver.observe(this.$refs.logosSection)
      }
    },

    // scrolling control for accreditation logos
    scrollBy(amount) {
      const wrap = this.$refs.logoWrapper
      if (wrap) {
        wrap.scrollBy({ left: amount, behavior: 'smooth' })
      }
    },

    startAutoScroll() {
      if (this.autoScrollTimer || !this.$refs.logoWrapper) return
      const wrap = this.$refs.logoWrapper
      const halfWidth = wrap.scrollWidth / 2

      const step = () => {
        if (!wrap) return
        let next = wrap.scrollLeft + 1
        if (next >= halfWidth) {
          // wrap by subtracting half so content appears continuous
          next -= halfWidth
        }
        wrap.scrollLeft = next
        this.autoScrollTimer = requestAnimationFrame(step)
      }

      this.autoScrollTimer = requestAnimationFrame(step)
    },

    stopAutoScroll() {
      if (this.autoScrollTimer) {
        cancelAnimationFrame(this.autoScrollTimer)
        clearTimeout(this.autoScrollTimer)
        this.autoScrollTimer = null
      }
    },

    // compensate scroll for manual interaction to keep infinite effect
    onLogoScroll() {
      const wrap = this.$refs.logoWrapper
      if (!wrap) return
      const halfWidth = wrap.scrollWidth / 2
      if (wrap.scrollLeft >= halfWidth) {
        wrap.scrollLeft -= halfWidth
      }
    },

    // arrow-driven manual scroll
    arrowDown(amount) {
      // stop automatic scrolling and start manual arrow scrolling
      this.stopAutoScroll()
      this.startArrowScroll(amount)
    },
    arrowUp() {
      // halt arrow scrolling and resume auto if not already running
      this.stopArrowScroll()
      this.startAutoScroll()
    },

    startArrowScroll(amount) {
      if (this.arrowTimer || !this.$refs.logoWrapper) return
      const wrap = this.$refs.logoWrapper
      const halfWidth = wrap.scrollWidth / 2
      const speed = amount > 0 ? 3 : -3

      const loop = () => {
        if (!wrap) return
        let pos = wrap.scrollLeft + speed
        if (pos >= halfWidth) pos -= halfWidth
        if (pos < 0) pos += halfWidth
        wrap.scrollLeft = pos
        this.arrowTimer = requestAnimationFrame(loop)
      }

      this.arrowTimer = requestAnimationFrame(loop)
    },

    stopArrowScroll() {
      if (this.arrowTimer) {
        // timer may have been created by requestAnimationFrame or setInterval
        cancelAnimationFrame(this.arrowTimer)
        clearInterval(this.arrowTimer)
        this.arrowTimer = null
      }
    }
  },
  watch: {
    logosVisible(val) {
      if (val) this.startAutoScroll()
      else this.stopAutoScroll()
    }
  }
}
</script>

<style scoped>
.prose {
  color: #374151;
}
.prose p {
  margin-bottom: 1.5rem;
}

/* Fade In Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-fadeIn {
  animation: fadeIn 0.8s ease-in-out both;
}

/* Fade In Up Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.8s ease-out both;
}

/* Slide In Up Animation */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slideInUp {
  animation: slideInUp 0.8s ease-out both;
}

/* Scale In Up Animation */
@keyframes scaleInUp {
  from {
    opacity: 0;
    transform: scaleY(0.8) translateY(30px);
  }
  to {
    opacity: 1;
    transform: scaleY(1) translateY(0);
  }
}

.animate-scaleInUp {
  animation: scaleInUp 0.7s ease-out both;
}

/* Scale In Animation */
@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-scaleIn {
  animation: scaleIn 0.6s ease-out both;
}

/* hide native scrollbar for horizontal wrapper */
.no-scrollbar {
  scrollbar-width: none; /* firefox */
}
.no-scrollbar::-webkit-scrollbar {
  display: none; /* webkit */
}
</style>